import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PILLAR_ARTICLES } from "@/mocks/articlesMeta";
import { ARTICLES_EN } from "@/mocks/articlesEn";
import { ARTICLES_FR } from "@/mocks/articlesFr";
import { ARTICLES_DA } from "@/mocks/articlesDa";
import { ARTICLES_DE } from "@/mocks/articlesDe";

interface ArticleSummary {
  title: string;
  excerpt: string;
}

const CONTENT_MAPS: Record<string, Record<string, ArticleSummary>> = {
  en: ARTICLES_EN as unknown as Record<string, ArticleSummary>,
  fr: ARTICLES_FR as unknown as Record<string, ArticleSummary>,
  da: ARTICLES_DA as unknown as Record<string, ArticleSummary>,
  de: ARTICLES_DE as unknown as Record<string, ArticleSummary>,
};

function pickLang(language: string): string {
  if (language?.startsWith("fr")) return "fr";
  if (language?.startsWith("da")) return "da";
  if (language?.startsWith("de")) return "de";
  return "en";
}

export default function ArticlesGrid() {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
      {PILLAR_ARTICLES.map((a) => {
        const content = CONTENT_MAPS[lang]?.[a.slug] ?? CONTENT_MAPS.en[a.slug];
        return (
          <Link
            key={a.slug}
            to={`/article/${a.slug}`}
            className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
          >
            <div className="relative w-full aspect-video overflow-hidden bg-background-100">
              <img
                src={a.image}
                alt={`${content.title} — AllChinaBuy Spreadsheet`}
                title={`${content.title} — AllChinaBuy Spreadsheet`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-md bg-background-50/90 text-primary-600">
                <i className={`${a.icon} text-base`} />
              </span>
            </div>
            <div className="flex flex-col flex-1 p-4">
              <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug">
                {content.title}
              </h3>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                {content.excerpt}
              </p>
              <span className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
                {t("articles.read")}
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}